import { useAuth } from '../../contexts/AuthContext';
import React, { useState, useEffect } from 'react';
import { Download, Calendar } from 'lucide-react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { buildApiUrl } from '../../utils/apiSetup';

const SalaryPaymentSummaryView = () => {
  const { hasPermission } = useAuth();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  
  const [selectedMonth, setSelectedMonth] = useState(currentMonth.toString());
  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const months = [
    'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
    'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
  ];

  const years = Array.from({ length: 10 }, (_, i) => (currentYear - 5 + i).toString());

  const monthName = months[parseInt(selectedMonth)];
  const title = reportData ? reportData.title : `SALARY FOR THE MONTH OF ${monthName} ${selectedYear}`;

  // Fetch report data when month/year changes
  useEffect(() => {
    const controller = new AbortController();
    const fetchReportData = async () => {
      setLoading(true);
      setError(null);
      try {
        const month = parseInt(selectedMonth) + 1; // Convert 0-based to 1-based
        const year = parseInt(selectedYear);
        const response = await fetch(buildApiUrl(`/api/v1/reports/salary-payment-summary?month=${month}&year=${year}`), {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
          signal: controller.signal,
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.detail || 'Failed to load report data');
        }
        const data = await response.json();
        setReportData(data);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error fetching salary payment summary:', err);
          setError(err.message || 'Failed to load report data');
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchReportData();
    return () => controller.abort();
  }, [selectedMonth, selectedYear]);

  const paymentData = reportData ? reportData.payments.map(p => ({
    srNo: p.srNo,
    particular: p.particular,
    amount: p.amount,
    chequeNo: '',
    date: ''
  })) : [];

  const totalAmount = reportData ? reportData.total : paymentData.reduce((sum, item) => sum + (item.amount || 0), 0);

  const extraItems = reportData && reportData.extra_items ? reportData.extra_items.map(item => ({
    srNo: item.srNo,
    particular: item.particular,
    amount: item.amount,
    chequeNo: '',
    date: ''
  })) : [];

  const grandTotal = reportData ? (reportData.grand_total || totalAmount) : totalAmount;

  const formatAmount = (num) => {
    if (num === null || num === undefined) return '0';
    return Number(num).toLocaleString('en-IN');
  };

  const handleExport = () => {
    const titleRow = [[title]];
    const headers = ['SR. NO.', 'PARTICULAR OF PAYMENT', 'AMOUNT', 'CHEQUE NO.', 'DATE'];
    
    const dataRows = paymentData.map(row => [
      row.srNo, row.particular, row.amount, row.chequeNo, row.date
    ]);

    const totalRow = ['', 'TOTAL', totalAmount, '', ''];

    const extraRows = extraItems.map(row => [
      row.srNo, row.particular, row.amount, row.chequeNo, row.date
    ]);

    const grandTotalRow = extraItems.length > 0 ? [['', 'GRAND TOTAL', grandTotal, '', '']] : [];

    const signaturesSpace1 = [];
    const signaturesSpace2 = [];
    const signaturesSpace3 = [];
    const signaturesRow1 = ['', '', '', 'CHIEF ACCOUNTANT', ''];
    const signaturesRow2 = ['', '', 'CHIEF EXECUTIVE OFFICER, PUNE.', '', ''];

    const sheetData = [
      titleRow[0],
      [], // Empty row
      headers,
      ...dataRows,
      totalRow,
      ...extraRows,
      ...grandTotalRow,
      signaturesSpace1,
      signaturesSpace2,
      signaturesSpace3,
      signaturesRow1,
      signaturesSpace1,
      signaturesSpace2,
      signaturesRow2
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    
    // Merge cells for title
    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Salary Payment Summary');
    
    XLSX.writeFile(workbook, `Salary_Payment_Summary_${monthName}_${selectedYear}.xlsx`);
  };

  const getThClass = () => `border border-gray-400 px-3 py-2 text-sm font-semibold text-center whitespace-nowrap bg-white text-gray-800`;
  const getTdClass = (isNumeric = false, isCenter = false) => `border border-gray-400 px-3 py-2 text-sm ${isNumeric ? 'text-right font-mono' : (isCenter ? 'text-center' : 'text-left')} text-gray-800 bg-white`;

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Filters and Actions */}
      <div className="p-4 border-b border-gray-200 flex flex-wrap gap-4 items-end justify-between bg-gray-50">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Month
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white min-w-[140px]"
            >
              {months.map((m, idx) => (
                <option key={idx} value={idx}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Year
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white min-w-[100px]"
            >
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div>
          {hasPermission('report:export') && (
            <button
              onClick={handleExport}
              className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Excel
            </button>
          )}
        </div>
      </div>

      {/* Table Container */}
      <div className="flex-1 overflow-auto p-6 flex justify-center bg-gray-100">
        <div className="inline-block align-middle pb-10 w-full max-w-4xl">
          <div className="bg-white p-8 shadow-sm border border-gray-300">
            {loading && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-4 text-sm text-gray-600">Loading report data...</p>
              </div>
            )}
            {error && (
              <div className="text-center py-12">
                <p className="text-red-600 font-medium">{error}</p>
                <p className="text-sm text-gray-500 mt-2">Please select a different month/year or contact support.</p>
              </div>
            )}
            {!loading && !error && (
              <>
                <h2 className="text-center font-bold text-lg mb-6 underline uppercase decoration-2 underline-offset-4">{title}</h2>
            
                <table className="w-full border-collapse border border-gray-400">
                  <thead>
                    <tr>
                      <th className={getThClass()}>SR. NO.</th>
                      <th className={getThClass()}>PARTICULAR OF PAYMENT</th>
                      <th className={getThClass()}>AMOUNT</th>
                      <th className={getThClass()}>CHEQUE NO.</th>
                      <th className={getThClass()}>DATE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentData.map((row, idx) => (
                      <tr key={idx}>
                        <td className={getTdClass(false, true)}>{row.srNo}</td>
                        <td className={getTdClass()}>{row.particular}</td>
                        <td className={getTdClass(true)}>{formatAmount(row.amount)}</td>
                        <td className={getTdClass(false, true)}>{row.chequeNo || '-'}</td>
                        <td className={getTdClass(false, true)}>{row.date || '-'}</td>
                      </tr>
                    ))}
                    
                    {/* Total Row */}
                    <tr>
                      <td className="border border-gray-400 px-3 py-2 bg-white"></td>
                      <td className="border border-gray-400 px-3 py-2 text-sm font-bold bg-white text-gray-800">TOTAL</td>
                      <td className="border border-gray-400 px-3 py-2 text-sm font-bold text-right font-mono bg-white text-gray-800">{formatAmount(totalAmount)}</td>
                      <td className="border border-gray-400 px-3 py-2 bg-white text-center text-sm text-gray-800">-</td>
                      <td className="border border-gray-400 px-3 py-2 bg-white text-center text-sm text-gray-800">-</td>
                    </tr>
                    
                    {/* Extra Rows */}
                    {extraItems.map((row, idx) => (
                      <tr key={`extra-${idx}`}>
                        <td className={getTdClass(false, true)}>{row.srNo}</td>
                        <td className={getTdClass()}>{row.particular}</td>
                        <td className={getTdClass(true)}>{formatAmount(row.amount)}</td>
                        <td className={getTdClass(false, true)}>{row.chequeNo || '-'}</td>
                        <td className={getTdClass(false, true)}>{row.date || '-'}</td>
                      </tr>
                    ))}
                    
                    {/* Grand Total Row */}
                    {extraItems.length > 0 && (
                      <tr>
                        <td className="border border-gray-400 px-3 py-2 bg-white"></td>
                        <td className="border border-gray-400 px-3 py-2 text-sm font-bold bg-white text-gray-800">GRAND TOTAL</td>
                        <td className="border border-gray-400 px-3 py-2 text-sm font-bold text-right font-mono bg-white text-gray-800">{formatAmount(grandTotal)}</td>
                        <td className="border border-gray-400 px-3 py-2 bg-white text-center text-sm text-gray-800">-</td>
                        <td className="border border-gray-400 px-3 py-2 bg-white text-center text-sm text-gray-800">-</td>
                      </tr>
                    )}
                  </tbody>
                </table>
            
            {/* Clean Footer Section */}
            <div className="w-full mt-24 pb-12 flex flex-col items-center">
              <div className="w-full flex justify-end pr-12 mb-20">
                <div className="font-bold text-sm text-gray-800">CHIEF ACCOUNTANT</div>
              </div>
              <div className="w-full flex justify-center">
                <div className="font-bold text-base text-gray-800 pl-24">CHIEF EXECUTIVE OFFICER, PUNE.</div>
              </div>
            </div>
            </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalaryPaymentSummaryView;
